import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    CaretLeft,
    CircleNotch,
    CurrencyDollar,
    Info,
    Sparkle,
    Tree,
    WarningCircle,
} from '@phosphor-icons/react';
import { PreContractSelectedModulesPopover } from './PreContractSelectedModulesPopover';
import {
    buildModuleProductionRankMap,
    type ModuleProductionRank,
} from '../lib/preContractSelectedModules';
import {
    orderIncludedModuleIdsForFill,
    parsePreContractTargetKwh,
} from '../lib/preContractAiDesigns';
import type { ModuleTypeOptionData } from '../../@types/types';
import moduleServices from '../../services/moduleService';
import {
    expandLayoutsToMaxFill,
    loadPreContractFullModuleLayouts,
} from '../lib/preContractFullModuleLayouts';
import {
    getPreContractModuleShadeColor,
    getPreContractShadeColor,
    isPreContractPanelAllowed,
    isPreContractPurplePanel,
    isPreContractShadePending,
} from '../lib/preContractModuleLayout';
import {
    averageIncludedMultiplier,
    roofIncludedMultiplierDetail,
} from '../lib/preContractPpwPricing';
import type { PreContractModuleCell } from '../lib/preContractTypes';
import type {
    MaxfillTargetCardProps,
    PreContractLayoutTab,
    PreContractModuleStepProps,
    RoofModuleBoardProps,
    SiteTreeBoardProps,
    SiteTreeIconProps,
} from './PreContractModuleStep.types';
import { PreContractDesignConfigBar } from './PreContractDesignConfigBar';

const COARSE_SA_LEGEND = [
    { label: '<30% SA', sample: 25 },
    { label: '30–50% SA', sample: 40 },
    { label: '50–74% SA', sample: 60 },
    { label: '75–94% SA', sample: 80 },
    { label: '95–100% SA', sample: 96 },
] as const;

const SA_LEGEND_POPOVER_WIDTH = 280;
const SA_LEGEND_POPOVER_GAP = 10;
const SA_LEGEND_VIEWPORT_MARGIN = 8;
const MODULE_STATUS_HIDE_MS = 2000;

export const PreContractModuleStep: React.FC<PreContractModuleStepProps> = ({
    form,
    patchForm,
    onBack,
    onRunAiDesign,
    onOpenPricing,
    actionsDisabled = false,
    runAiDisabled,
    runAiIssues,
    hasPreviousAiDesigns = false,
    viewportSlot,
    renderViewport = true,
    embedded = false,
    solarPanelChild = false,
    hideBody = false,
    onShowBody,
    onTreeCuttingActiveChange,
    onTreeGhostChange,
}) => {
    const [moduleOptions, setModuleOptions] = useState<ModuleTypeOptionData[]>([]);
    const [moduleLoadError, setModuleLoadError] = useState<string | null>(null);
    const [hoverCell, setHoverCell] = useState<{
        roofIndex: number;
        moduleIndex: number;
    } | null>(null);
    const [moduleStatusOpen, setModuleStatusOpen] = useState(false);
    const moduleStatusHideTimerRef = useRef<number | null>(null);
    const [layoutTab, setLayoutTab] = useState<PreContractLayoutTab>('modules');
    const [selectedModulesOpen, setSelectedModulesOpen] = useState(false);

    useEffect(() => {
        onTreeCuttingActiveChange?.(layoutTab === 'trees');
        return () => onTreeCuttingActiveChange?.(false);
    }, [layoutTab, onTreeCuttingActiveChange]);

    useEffect(() => {
        if (hideBody) setLayoutTab('modules');
    }, [hideBody]);

    const allRoofBoards = useMemo(
        () =>
            expandLayoutsToMaxFill(
                form.roofModuleLayouts,
                loadPreContractFullModuleLayouts(),
                {
                    includeGreyPanels: form.includeGreyPanels,
                    includePurplePanels: form.includePurplePanels,
                },
            ),
        [
            form.includeGreyPanels,
            form.includePurplePanels,
            form.roofModuleLayouts,
        ],
    );
    const configRoofSelections = useMemo(
        () => selectionsFromLayouts(allRoofBoards),
        [allRoofBoards],
    );

    const shadeLoad = useMemo(() => {
        let present = 0;
        let pending = 0;
        allRoofBoards.forEach((layout) => {
            layout.modules.forEach((cell) => {
                if (!cell) return;
                present += 1;
                if (isPreContractShadePending(cell.solarAccess)) pending += 1;
            });
        });
        return { present, pending };
    }, [allRoofBoards]);
    const modulesLoading = shadeLoad.present === 0 || shadeLoad.pending > 0;
    const anyRoofActive = useMemo(
        () => allRoofBoards.some((layout) => layoutIsActive(layout)),
        [allRoofBoards],
    );
    const systemMultiplier = useMemo(
        () => averageIncludedMultiplier(allRoofBoards, configRoofSelections),
        [allRoofBoards, configRoofSelections],
    );
    const moduleFillOrderIds = useMemo(
        () => orderIncludedModuleIdsForFill(form, allRoofBoards, configRoofSelections),
        [allRoofBoards, configRoofSelections, form],
    );
    const targetUsageKwh = useMemo(
        () => parsePreContractTargetKwh(form.annualUsageKwh),
        [form.annualUsageKwh],
    );
    const saLegendTriggerRef = useRef<HTMLButtonElement>(null);
    const saLegendPopoverRef = useRef<HTMLDivElement>(null);
    const saLegendCloseTimerRef = useRef<number | null>(null);
    const [saLegendOpen, setSaLegendOpen] = useState(false);
    const [saLegendPos, setSaLegendPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);

    const clearSaLegendCloseTimer = useCallback(() => {
        if (saLegendCloseTimerRef.current != null) {
            window.clearTimeout(saLegendCloseTimerRef.current);
            saLegendCloseTimerRef.current = null;
        }
    }, []);

    const openSaLegend = useCallback(() => {
        clearSaLegendCloseTimer();
        setSaLegendOpen(true);
    }, [clearSaLegendCloseTimer]);

    const scheduleCloseSaLegend = useCallback(() => {
        clearSaLegendCloseTimer();
        saLegendCloseTimerRef.current = window.setTimeout(() => setSaLegendOpen(false), 120);
    }, [clearSaLegendCloseTimer]);

    const updateSaLegendPopoverPosition = useCallback(() => {
        const trigger = saLegendTriggerRef.current;
        if (!trigger) return;

        const triggerRect = trigger.getBoundingClientRect();
        const popoverWidth = saLegendPopoverRef.current?.offsetWidth ?? SA_LEGEND_POPOVER_WIDTH;
        const popoverHeight = saLegendPopoverRef.current?.offsetHeight ?? 220;

        let left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - SA_LEGEND_VIEWPORT_MARGIN;
        left = Math.max(SA_LEGEND_VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow =
            window.innerHeight - triggerRect.bottom - SA_LEGEND_POPOVER_GAP - SA_LEGEND_VIEWPORT_MARGIN;
        const spaceAbove = triggerRect.top - SA_LEGEND_POPOVER_GAP - SA_LEGEND_VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? triggerRect.bottom + SA_LEGEND_POPOVER_GAP
                : triggerRect.top - popoverHeight - SA_LEGEND_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - SA_LEGEND_VIEWPORT_MARGIN;
        top = Math.max(SA_LEGEND_VIEWPORT_MARGIN, Math.min(top, maxTop));

        setSaLegendPos({ top, left, placement });
    }, []);

    useLayoutEffect(() => {
        if (!saLegendOpen) {
            setSaLegendPos(null);
            return;
        }
        updateSaLegendPopoverPosition();
        const frame = requestAnimationFrame(() => updateSaLegendPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [saLegendOpen, updateSaLegendPopoverPosition]);

    useEffect(() => {
        if (!saLegendOpen) return undefined;
        const onReflow = () => updateSaLegendPopoverPosition();
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);
        return () => {
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [saLegendOpen, updateSaLegendPopoverPosition]);

    useEffect(() => () => clearSaLegendCloseTimer(), [clearSaLegendCloseTimer]);

    const saLegendPopoverContent = (
        <div className="pci-sa-legend pci-sa-legend--popover" aria-label="Solar access legend">
            {COARSE_SA_LEGEND.map((band) => (
                <span key={band.label} className="pci-sa-legend__item">
                    <i style={{ background: getPreContractShadeColor(band.sample) }} />
                    {band.label}
                </span>
            ))}
            <span className="pci-sa-legend__item">
                <i className="pci-sa-legend__hatch" />
                Not used
            </span>
            <span className="pci-sa-legend__item">
                <i className="pci-sa-legend__loading" />
                Loading
            </span>
        </div>
    );

    const saLegendPopover =
        saLegendOpen
            ? createPortal(
                  <div
                      ref={saLegendPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal pci-sa-legend-popover${
                          saLegendPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="tooltip"
                      aria-label="Solar access legend"
                      style={{
                          top: saLegendPos?.top ?? -9999,
                          left: saLegendPos?.left ?? -9999,
                          visibility: saLegendPos ? 'visible' : 'hidden',
                      }}
                      onMouseEnter={openSaLegend}
                      onMouseLeave={scheduleCloseSaLegend}
                  >
                      {saLegendPopoverContent}
                  </div>,
                  document.body,
              )
            : null;

    const saLegendInfoButton = (
        <>
            <button
                ref={saLegendTriggerRef}
                type="button"
                className={`pci-sa-legend-info${
                    embedded ? ' pci-sa-legend-info--toolbar' : ''
                }`}
                aria-label="Solar access legend"
                aria-expanded={saLegendOpen}
                onMouseEnter={openSaLegend}
                onMouseLeave={scheduleCloseSaLegend}
                onFocus={openSaLegend}
                onBlur={scheduleCloseSaLegend}
            >
                <Info size={14} weight="fill" aria-hidden />
            </button>
            {saLegendPopover}
        </>
    );

    const moduleSaLegendFooter = embedded ? null : (
        <div className="pci-sa-legend-footer">
            <span className="pci-sa-legend__item pci-sa-legend__item--system-mult">
                System mult{' '}
                <strong>{systemMultiplier != null ? systemMultiplier.toFixed(3) : '—'}</strong>
            </span>
            {saLegendInfoButton}
        </div>
    );
    const runAiBlocked = runAiDisabled ?? actionsDisabled;
    const runNewDisabled = runAiBlocked || modulesLoading || !anyRoofActive;
    const viewPreviousDisabled = actionsDisabled;

    const runAiTitle = useMemo(() => {
        if (modulesLoading) return 'Wait until all modules finish loading shade';
        if (!anyRoofActive) return 'Select at least one module to run AI designs';
        if (runAiBlocked) {
            return runAiIssues?.length
                ? `Cannot run AI yet: ${runAiIssues.join(' · ')}`
                : 'Complete required intake steps (products, site, utility, design) to run AI';
        }
        return hasPreviousAiDesigns ? 'Run new AI designs' : 'Run AI designs';
    }, [modulesLoading, anyRoofActive, runAiBlocked, runAiIssues, hasPreviousAiDesigns]);

    useEffect(() => {
        let cancelled = false;
        const stateCode = form.installState || 'CT';

        void (async () => {
            try {
                const res = await moduleServices.getModuleList(stateCode);
                const modules = (res?.Modules ?? []) as Array<{
                    Name: string;
                    Wattage: number;
                    WidthInMm: number;
                    LengthInMm: number;
                    Manufacturer: string;
                }>;
                if (cancelled) return;
                const options: ModuleTypeOptionData[] = modules.map((m, index) => ({
                    key: index,
                    text: m.Name,
                    value: m.Name,
                    power: m.Wattage,
                    w: m.WidthInMm,
                    h: m.LengthInMm,
                    manufacturer: m.Manufacturer,
                }));
                setModuleOptions(options);
                setModuleLoadError(null);
                if (!form.moduleTypeName && options.length > 0) {
                    const preferred =
                        options.find((o) => /410/.test(o.text)) ?? options[0];
                    patchForm({
                        moduleTypeName: preferred.text,
                        moduleTypeWattage: preferred.power,
                    });
                }
            } catch {
                if (!cancelled) {
                    setModuleLoadError('Could not load module types for this state.');
                    setModuleOptions([
                        {
                            key: 0,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 410',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 410',
                            power: 410,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                        {
                            key: 1,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 405',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 405',
                            power: 405,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                        {
                            key: 2,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10+ 400',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10+ 400',
                            power: 400,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                    ]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // Intentionally only re-fetch when state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.installState]);

    const cellBlocked = (cell: { solarAccess: number } | null | undefined) =>
        Boolean(cell) &&
        !isPreContractPanelAllowed(
            cell!.solarAccess,
            form.includeGreyPanels,
            form.includePurplePanels,
        );

    /** Grey/purple panels cannot stay selected while their toggle is off. */
    useEffect(() => {
        let changed = false;
        const nextLayouts = form.roofModuleLayouts.map((layout) => ({
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell?.included || !cellBlocked(cell)) return cell;
                changed = true;
                return { ...cell, included: false };
            }),
        }));
        if (!changed) return;
        patchForm({
            roofModuleLayouts: nextLayouts,
            roofSelections: selectionsFromLayouts(nextLayouts),
        });
    }, [form.includeGreyPanels, form.includePurplePanels, form.roofModuleLayouts]);

    const toggleModule = (roofIndex: number, moduleIndex: number) => {
        const target = allRoofBoards.find((layout) => layout.roofIndex === roofIndex)
            ?.modules[moduleIndex];
        if (!target) return;
        if (!target.included && cellBlocked(target)) return;
        const nextLayouts = allRoofBoards.map((layout) => {
            if (layout.roofIndex !== roofIndex) return layout;
            return {
                ...layout,
                modules: layout.modules.map((cell, idx) => {
                    if (idx !== moduleIndex || !cell) return cell;
                    return { ...cell, included: !cell.included };
                }),
            };
        });
        patchForm({
            roofModuleLayouts: nextLayouts,
            roofSelections: selectionsFromLayouts(nextLayouts),
        });
    };

    const setRoofModulesIncluded = (roofIndex: number, included: boolean) => {
        const nextLayouts = allRoofBoards.map((layout) => {
            if (layout.roofIndex !== roofIndex) return layout;
            return {
                ...layout,
                modules: layout.modules.map((cell) => {
                    if (!cell) return null;
                    if (included && cellBlocked(cell)) return { ...cell, included: false };
                    return { ...cell, included };
                }),
            };
        });
        patchForm({
            roofSelections: selectionsFromLayouts(nextLayouts),
            roofModuleLayouts: nextLayouts,
        });
    };

    const toggleTreeGhost = (treeId: string) => {
        const siteTrees = form.siteTrees.map((tree) =>
            tree.id === treeId ? { ...tree, ghosted: !tree.ghosted } : tree,
        );
        patchForm({ siteTrees, treeCount: siteTrees.length });
        onTreeGhostChange?.(siteTrees);
    };

    const showModulesTab = () => {
        if (hideBody) onShowBody?.();
        setLayoutTab('modules');
    };
    const showTreesTab = () => {
        if (hideBody) onShowBody?.();
        setLayoutTab('trees');
    };

    const clearModuleStatusHideTimer = useCallback(() => {
        if (moduleStatusHideTimerRef.current != null) {
            window.clearTimeout(moduleStatusHideTimerRef.current);
            moduleStatusHideTimerRef.current = null;
        }
    }, []);

    const showModuleStatus = useCallback(
        (cell: { roofIndex: number; moduleIndex: number }) => {
            clearModuleStatusHideTimer();
            setHoverCell(cell);
            setModuleStatusOpen(true);
        },
        [clearModuleStatusHideTimer],
    );

    const scheduleHideModuleStatus = useCallback(() => {
        clearModuleStatusHideTimer();
        moduleStatusHideTimerRef.current = window.setTimeout(() => {
            setModuleStatusOpen(false);
            moduleStatusHideTimerRef.current = null;
        }, MODULE_STATUS_HIDE_MS);
    }, [clearModuleStatusHideTimer]);

    useEffect(
        () => () => clearModuleStatusHideTimer(),
        [clearModuleStatusHideTimer],
    );

    useEffect(() => {
        if (layoutTab === 'modules') return;
        clearModuleStatusHideTimer();
        setModuleStatusOpen(false);
    }, [clearModuleStatusHideTimer, layoutTab]);

    const activeModuleCell = useMemo(() => {
        if (!hoverCell) return null;
        const layout = allRoofBoards.find((l) => l.roofIndex === hoverCell.roofIndex);
        const cell = layout?.modules[hoverCell.moduleIndex];
        if (cell && 'id' in cell) return hoverCell;
        return null;
    }, [hoverCell, allRoofBoards]);

    const hoverLayout = activeModuleCell
        ? allRoofBoards.find((l) => l.roofIndex === activeModuleCell.roofIndex)
        : null;
    const hoverModule =
        hoverLayout && activeModuleCell
            ? hoverLayout.modules[activeModuleCell.moduleIndex]
            : null;
    const hoverModuleCell = hoverModule && 'id' in hoverModule ? hoverModule : null;

    const moduleProductionRankMap = useMemo(
        () => buildModuleProductionRankMap(allRoofBoards),
        [allRoofBoards],
    );
    const hoverProductionRank = hoverModuleCell
        ? moduleProductionRankMap.get(hoverModuleCell.id) ?? null
        : null;

    const configBar = (
        <PreContractDesignConfigBar
            form={form}
            patchForm={patchForm}
            compact={false}
            dense={embedded}
        />
    );

    const runAiButton = (
        <button
            type="button"
            className="pci-layout-toolbar__run-ai"
            disabled={runNewDisabled}
            title={runAiTitle}
            onClick={() => onRunAiDesign(false)}
        >
            <Sparkle size={embedded ? 14 : 16} weight="fill" aria-hidden />
            {modulesLoading ? 'Waiting…' : 'Run AI'}
        </button>
    );

    const selectedModulesControl =
        layoutTab === 'modules' ? (
            <PreContractSelectedModulesPopover
                layouts={allRoofBoards}
                allModulesLayouts={allRoofBoards}
                roofSelections={configRoofSelections}
                fillOrderIds={moduleFillOrderIds}
                targetKwh={targetUsageKwh}
                escalator={form.targetEscalator}
                rate={form.targetMinPpaRate}
                form={form}
                open={selectedModulesOpen}
                onOpenChange={setSelectedModulesOpen}
                compact={embedded}
                disabled={modulesLoading}
            />
        ) : null;

    const layoutToolbarActions = embedded ? (
        <div className="pci-module-panel__action-cluster">
            {selectedModulesControl}
            {runAiButton}
            {layoutTab === 'modules' ? saLegendInfoButton : null}
        </div>
    ) : null;

    const layoutPanelBody = (
        <>
                    <div
                        className={
                            embedded
                                ? 'pci-module-panel__chrome'
                                : `pci-layout-toolbar${
                                      embedded ? ' pci-layout-toolbar--embedded' : ''
                                  }`
                        }
                    >
                        {!embedded ? (
                            <div
                                className={`pci-layout-toolbar__row pci-layout-toolbar__row--head pci-layout-toolbar__row--v2${
                                    embedded ? ' pci-layout-toolbar__row--primary' : ''
                                }`}
                            >
                                <div
                                    className={`pci-layout-tabs pci-layout-tabs--segmented${
                                        embedded ? ' pci-layout-tabs--compact' : ''
                                    }`}
                                    role="tablist"
                                    aria-label="Layout views"
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        className={`pci-layout-tabs__btn${
                                            layoutTab === 'modules' ? ' pci-layout-tabs__btn--on' : ''
                                        }`}
                                        aria-selected={layoutTab === 'modules'}
                                        onClick={showModulesTab}
                                    >
                                        <Sparkle size={14} weight="duotone" aria-hidden />
                                        Module layout
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        className={`pci-layout-tabs__btn${
                                            layoutTab === 'trees' ? ' pci-layout-tabs__btn--on' : ''
                                        }`}
                                        aria-selected={layoutTab === 'trees'}
                                        onClick={showTreesTab}
                                    >
                                        <Tree size={14} weight="duotone" aria-hidden />
                                        Tree cutting
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="pci-module-panel__toolbar">
                                <div className="pci-module-panel__toolbar-group pci-module-panel__toolbar-group--views">
                                    <div
                                        className="pci-module-panel__view-tabs"
                                        role="tablist"
                                        aria-label="Layout views"
                                    >
                                        <button
                                            type="button"
                                            role="tab"
                                            className={`pci-module-panel__view-tab${
                                                layoutTab === 'modules'
                                                    ? ' pci-module-panel__view-tab--on'
                                                    : ''
                                            }`}
                                            aria-selected={layoutTab === 'modules'}
                                            onClick={showModulesTab}
                                        >
                                            <Sparkle size={13} weight="duotone" aria-hidden />
                                            Modules
                                        </button>
                                        <button
                                            type="button"
                                            role="tab"
                                            className={`pci-module-panel__view-tab${
                                                layoutTab === 'trees'
                                                    ? ' pci-module-panel__view-tab--on'
                                                    : ''
                                            }`}
                                            aria-selected={layoutTab === 'trees'}
                                            onClick={showTreesTab}
                                        >
                                            <Tree size={13} weight="duotone" aria-hidden />
                                            Trees
                                        </button>
                                    </div>
                                </div>
                                {layoutToolbarActions}
                            </div>
                        )}
                        {layoutTab === 'modules' || hideBody ? (
                            <div
                                className={
                                    embedded
                                        ? 'pci-module-panel__config'
                                        : 'pci-layout-toolbar__row pci-layout-toolbar__row--config'
                                }
                            >
                                {configBar}
                            </div>
                        ) : null}
                        {!embedded ? (
                            <div className="pci-layout-toolbar__actions pci-layout-toolbar__actions--v2">
                                {selectedModulesControl}
                                {runAiButton}
                            </div>
                        ) : null}
                    </div>
                {hideBody ? null : layoutTab === 'modules' ? (
                    <>
                <div
                    className={
                        embedded ? 'pci-module-panel__scroll' : undefined
                    }
                >
                <div
                    className="pci-module-layout-grid"
                    onMouseLeave={scheduleHideModuleStatus}
                >
                    {allRoofBoards.length > 0 ? (
                        allRoofBoards.map((layout) => {
                            const roofActive = layoutIsActive(layout);
                            const multiplierDetail = roofActive
                                ? roofIncludedMultiplierDetail(layout)
                                : {
                                      multiplier: null,
                                      meanSolarAccess: null,
                                      includedCount: 0,
                                  };
                            return (
                                <RoofModuleBoard
                                    key={`${layout.roofIndex}-${layout.label}`}
                                    layout={layout}
                                    multiplierDetail={multiplierDetail}
                                    inactive={!roofActive}
                                    includeGreyPanels={form.includeGreyPanels}
                                    includePurplePanels={form.includePurplePanels}
                                    compact={embedded}
                                    onToggleRoof={() =>
                                        setRoofModulesIncluded(layout.roofIndex, !roofActive)
                                    }
                                    onToggle={(moduleIndex) =>
                                        toggleModule(layout.roofIndex, moduleIndex)
                                    }
                                    onHover={(moduleIndex) =>
                                        showModuleStatus({
                                            roofIndex: layout.roofIndex,
                                            moduleIndex,
                                        })
                                    }
                                />
                            );
                        })
                    ) : (
                        <p className="pci-module-step__empty-roofs">
                            Waiting for live roof modules from the 3D viewport…
                        </p>
                    )}
                </div>
                </div>

                {embedded ? (
                    hoverModuleCell && hoverLayout
                        ? (() => {
                              const statusCard = (
                                  <div
                                      className={`pci-module-panel__status pci-module-panel__status--viewport${
                                          moduleStatusOpen
                                              ? ' pci-module-panel__status--open'
                                              : ''
                                      }`}
                                      role="status"
                                      onMouseEnter={clearModuleStatusHideTimer}
                                      onMouseLeave={scheduleHideModuleStatus}
                                  >
                                      <ModuleHoverDetails
                                          cell={hoverModuleCell}
                                          roofLabel={hoverLayout.label}
                                          blocked={cellBlocked(hoverModuleCell)}
                                          productionRank={hoverProductionRank}
                                      />
                                  </div>
                              );
                              const viewportHost =
                                  typeof document !== 'undefined'
                                      ? document.getElementById('pci-onedraw-host') ??
                                        document.getElementById('pci-section-viewport')
                                      : null;
                              return viewportHost
                                  ? createPortal(statusCard, viewportHost)
                                  : statusCard;
                          })()
                        : null
                ) : (
                    <>
                        {hoverModuleCell && hoverLayout ? (
                            <div
                                className={`pci-module-tooltip${
                                    moduleStatusOpen ? ' pci-module-tooltip--open' : ''
                                }`}
                                role="status"
                                onMouseEnter={clearModuleStatusHideTimer}
                                onMouseLeave={scheduleHideModuleStatus}
                            >
                                <ModuleHoverDetails
                                    cell={hoverModuleCell}
                                    roofLabel={hoverLayout.label}
                                    blocked={cellBlocked(hoverModuleCell)}
                                    productionRank={hoverProductionRank}
                                />
                            </div>
                        ) : null}
                        {moduleSaLegendFooter}
                    </>
                )}
                    </>
                ) : (
                    <div className={embedded ? 'pci-module-panel__scroll' : undefined}>
                    <>
                        <div className="pci-tree-cutting-bar" aria-label="Tree cutting">
                            <p className="pci-tree-cutting-bar__hint">
                                Click a tree to ghost it in 3D
                            </p>
                            <div className="pci-sa-legend pci-sa-legend--inline" aria-hidden>
                                <span className="pci-sa-legend__item">
                                    <i className="pci-tree-swatch pci-tree-swatch--live" />
                                    Standing
                                </span>
                                <span className="pci-sa-legend__item">
                                    <i className="pci-tree-swatch pci-tree-swatch--ghost" />
                                    Ghosted
                                </span>
                            </div>
                        </div>
                        <SiteTreeBoard trees={form.siteTrees} onToggle={toggleTreeGhost} />
                    </>
                    </div>
                )}
        </>
    );

    if (hideBody) {
        return null;
    }

    if (solarPanelChild) {
        return layoutPanelBody;
    }

    return (
        <div
            id="pci-section-layout"
            className={`pci-module-step pci-module-step--v3${
                embedded ? ' pci-module-step--embedded' : ''
            }`}
        >
            {renderViewport ? viewportSlot : null}

            <section
                className={`pci-module-step__card pci-module-step__card--layout${
                    embedded ? ' pci-module-step__card--popup' : ''
                }`}
            >
                <div className={embedded ? 'pci-module-panel' : undefined}>
                    {layoutPanelBody}
                </div>
            </section>

            {!embedded ? (
            <div className="pci-board-fab-dock" aria-label="Board actions">
                <div className="pci-board-fab-dock__start">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                </div>
                <div className="pci-board-fab-dock__main" aria-hidden />
                <div className="pci-board-fab-dock__end">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onOpenPricing}
                        title="Pricing"
                        aria-label="Pricing"
                    >
                        <CurrencyDollar size={20} weight="bold" />
                    </button>
                    <div className="pci-board-fab__ai" role="group" aria-label="AI designs">
                        {hasPreviousAiDesigns ? (
                            <>
                                <button
                                    type="button"
                                    className="pci-board-fab__ai-btn"
                                    disabled={runNewDisabled}
                                    title={runAiTitle}
                                    onClick={() => onRunAiDesign(false)}
                                >
                                    Run AI Designs
                                </button>
                                <button
                                    type="button"
                                    className="pci-board-fab__ai-btn pci-board-fab__ai-btn--primary"
                                    disabled={viewPreviousDisabled}
                                    title="View previous AI designs"
                                    onClick={() => onRunAiDesign(true)}
                                >
                                    <Sparkle size={16} weight="fill" />
                                    View
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="pci-board-fab__ai-btn pci-board-fab__ai-btn--primary pci-board-fab__ai-btn--solo"
                                disabled={runNewDisabled}
                                title={runAiTitle}
                                onClick={() => onRunAiDesign(false)}
                            >
                                <Sparkle size={16} weight="fill" />
                                {modulesLoading ? 'Waiting…' : 'Run AI Designs'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            ) : null}
        </div>
    );
};

function layoutIsActive(layout: {
    modules: Array<{ included: boolean } | null>;
}): boolean {
    return layout.modules.some((cell) => Boolean(cell?.included));
}

function selectionsFromLayouts(
    layouts: Array<{ modules: Array<{ included: boolean } | null> }>,
): boolean[] {
    return layouts.map((layout) => layoutIsActive(layout));
}

function formatKwh(value: number): string {
    return `${Math.round(value).toLocaleString()} kWh`;
}

function moduleProductionKwh(cell: NonNullable<PreContractModuleCell>): number | null {
    const production = Number(cell.productionKwh);
    if (Number.isFinite(production) && production > 0) return Math.round(production);
    return null;
}

function moduleMultiplier(cell: NonNullable<PreContractModuleCell>): number | null {
    const multiplier = Number(cell.multiplier);
    if (Number.isFinite(multiplier) && multiplier > 0) return multiplier;
    return null;
}

type ModuleHoverDetailsProps = {
    cell: NonNullable<PreContractModuleCell>;
    roofLabel: string;
    blocked: boolean;
    productionRank: ModuleProductionRank | null;
};

function formatProductionRank(rank: ModuleProductionRank): string {
    if (rank.rank === 1) {
        return `#1 of ${rank.total} · Highest production`;
    }
    if (rank.rank === rank.total) {
        return `#${rank.rank} of ${rank.total} · Lowest production`;
    }
    const topPercent = Math.round(((rank.total - rank.rank + 1) / rank.total) * 100);
    return `#${rank.rank} of ${rank.total} · Top ${topPercent}%`;
}

function ModuleHoverDetails({ cell, roofLabel, blocked, productionRank }: ModuleHoverDetailsProps) {
    const shadePending = isPreContractShadePending(cell.solarAccess);
    const production = moduleProductionKwh(cell);
    const multiplier = moduleMultiplier(cell);
    const saColor = getPreContractModuleShadeColor(cell);

    let status: string;
    if (shadePending) {
        status = 'Loading shade…';
    } else if (blocked) {
        status = isPreContractPurplePanel(cell.solarAccess) ? 'Purple excluded' : 'Grey excluded';
    } else if (cell.included) {
        status = 'Included · click to remove';
    } else {
        status = 'Excluded · click to include';
    }

    return (
        <div className="pci-module-hover-card">
            <p className="pci-module-hover-card__title">{cell.id}</p>
            <dl className="pci-module-hover-card__list">
                <div className="pci-module-hover-card__row">
                    <dt>Roof</dt>
                    <dd>{roofLabel}</dd>
                </div>
                <div className="pci-module-hover-card__row">
                    <dt>Solar access</dt>
                    <dd>
                        {shadePending ? (
                            'Loading…'
                        ) : (
                            <em style={{ color: saColor }}>{cell.solarAccess.toFixed(1)}%</em>
                        )}
                    </dd>
                </div>
                <div className="pci-module-hover-card__row">
                    <dt>Multiplier</dt>
                    <dd>{shadePending || multiplier == null ? '—' : multiplier.toFixed(3)}</dd>
                </div>
                <div className="pci-module-hover-card__row">
                    <dt>Production</dt>
                    <dd>{shadePending || production == null ? '—' : formatKwh(production)}</dd>
                </div>
                <div
                    className={`pci-module-hover-card__row${
                        productionRank?.rank === 1
                            ? ' pci-module-hover-card__row--rank-lead'
                            : productionRank?.rank === productionRank?.total
                              ? ' pci-module-hover-card__row--rank-low'
                              : ''
                    }`}
                >
                    <dt>Production rank</dt>
                    <dd>
                        {shadePending || production == null || productionRank == null
                            ? '—'
                            : formatProductionRank(productionRank)}
                    </dd>
                </div>
                <div className="pci-module-hover-card__row">
                    <dt>Status</dt>
                    <dd>{status}</dd>
                </div>
            </dl>
        </div>
    );
}

const MaxfillTargetCard: React.FC<MaxfillTargetCardProps> = ({
    productionKwh,
    targetKwh,
    moduleCount,
    waitingForShade,
    usedLiveProduction,
}) => {
    const hasProduction = productionKwh != null && productionKwh > 0;
    const hasTarget = targetKwh != null && targetKwh > 0;
    const offsetPercent =
        hasProduction && hasTarget ? Math.round((productionKwh / targetKwh) * 100) : null;
    const gapKwh =
        hasProduction && hasTarget ? Math.round(productionKwh - targetKwh) : null;
    const barMax =
        hasProduction && hasTarget ? Math.max(productionKwh, targetKwh) : 0;
    const barPercent =
        hasProduction && hasTarget
            ? Math.max(4, Math.min(100, (productionKwh / barMax) * 100))
            : 0;
    const markPercent =
        hasProduction && hasTarget ? Math.min(100, (targetKwh / barMax) * 100) : 100;
    const closeness =
        waitingForShade
            ? 'Waiting for oneDRAW shade to calculate max-fill production.'
            : gapKwh == null
              ? hasTarget
                  ? 'Max-fill production is not available yet.'
                  : 'Add annual usage to see how close max-fill is to target.'
              : gapKwh === 0
                ? 'On target'
                : gapKwh > 0
                  ? `${formatKwh(gapKwh)} over target`
                  : `${formatKwh(Math.abs(gapKwh))} short of target`;
    const closenessTone =
        gapKwh == null ? '' : gapKwh >= 0 ? ' pci-maxfill-target__closeness--over' : ' pci-maxfill-target__closeness--short';
    const sourceLabel = waitingForShade
        ? 'Waiting for oneDRAW shade…'
        : usedLiveProduction
          ? 'From oneDRAW max-fill on active roofs'
          : 'Estimated from active-roof max-fill';

    return (
        <section className="pci-module-step__card pci-maxfill-target" aria-label="oneDRAW max-fill vs target">
            <p className="pci-board-v2-label">oneDRAW max-fill</p>
            <h2 className="pci-module-step__title">Production vs target</h2>
            <p className="pci-maxfill-target__lead">{sourceLabel}</p>
            <dl className="pci-maxfill-target__stats">
                <div>
                    <dt>Max-fill</dt>
                    <dd>{hasProduction ? formatKwh(productionKwh) : '—'}</dd>
                </div>
                <div>
                    <dt>Target</dt>
                    <dd>{hasTarget ? formatKwh(targetKwh) : '—'}</dd>
                </div>
                <div>
                    <dt>Offset</dt>
                    <dd>{offsetPercent != null ? `${offsetPercent}%` : '—'}</dd>
                </div>
                <div>
                    <dt>Modules</dt>
                    <dd>{moduleCount > 0 ? moduleCount : '—'}</dd>
                </div>
            </dl>
            {hasTarget && hasProduction ? (
                <div className="pci-maxfill-target__bar" aria-hidden>
                    <span className="pci-maxfill-target__bar-fill" style={{ width: `${barPercent}%` }} />
                    <span
                        className="pci-maxfill-target__bar-mark"
                        style={{ left: `${markPercent}%` }}
                        title="Target"
                    />
                </div>
            ) : null}
            <p className={`pci-maxfill-target__closeness${closenessTone}`}>{closeness}</p>
        </section>
    );
};

export function RoofModuleBoard({
    layout,
    multiplierDetail,
    inactive,
    includeGreyPanels,
    includePurplePanels,
    onToggleRoof,
    onToggle,
    onHover,
    compact = false,
    readOnly = false,
}: RoofModuleBoardProps) {
    const presentModules = layout.modules.filter((m): m is NonNullable<typeof m> => Boolean(m));
    const activeCount = presentModules.filter((m) => m.included).length;
    const isBlocked = (solarAccess: number) =>
        !isPreContractPanelAllowed(solarAccess, includeGreyPanels, includePurplePanels);
    const selectableCount = presentModules.filter((m) => !isBlocked(m.solarAccess)).length;
    const roofToggleDisabled = readOnly || (inactive && selectableCount === 0);
    const roofMultiplier = multiplierDetail.multiplier;

    return (
        <div
            className={`pci-roof-modules pci-roof-modules--${layout.moduleOrientation ?? 'portrait'}${
                inactive ? ' pci-roof-modules--unused' : ''
            }${compact ? ' pci-roof-modules--compact' : ''}`}
            style={{ ['--pci-roof-cols' as string]: String(layout.cols) }}
        >
            <header
                className={`pci-roof-modules__head${
                    compact ? ' pci-roof-modules__head--compact' : ''
                }`}
            >
                {compact ? (
                    <>
                        <div className="pci-roof-modules__head-row">
                            <strong className="pci-roof-modules__name">
                                {layout.label}
                                {inactive ? ' (inactive)' : ''}
                            </strong>
                            <button
                                type="button"
                                className={`pci-roof-modules__active-btn${
                                    inactive
                                        ? ' pci-roof-modules__active-btn--off'
                                        : ' pci-roof-modules__active-btn--on'
                                }`}
                                aria-pressed={!inactive}
                                disabled={roofToggleDisabled}
                                title={
                                    roofToggleDisabled
                                        ? 'Every panel on this roof is excluded by shaded-panel settings'
                                        : inactive
                                          ? 'Select all modules on this roof'
                                          : 'Deselect all modules on this roof'
                                }
                                onClick={
                                    readOnly
                                        ? (event) => event.stopPropagation()
                                        : onToggleRoof
                                }
                            >
                                {inactive ? 'Inactive' : 'Active'}
                            </button>
                        </div>
                        <div className="pci-roof-modules__stats">
                            <span className="pci-roof-modules__stat">
                                {activeCount}/{presentModules.length} active
                            </span>
                            <span
                                className="pci-roof-modules__stat pci-roof-modules__stat--mult"
                                title={
                                    multiplierDetail.meanSolarAccess != null
                                        ? `Avg SA ${multiplierDetail.meanSolarAccess.toFixed(1)}% across ${multiplierDetail.includedCount} included module${multiplierDetail.includedCount === 1 ? '' : 's'}`
                                        : 'Multiplier for included modules on this roof'
                                }
                            >
                                Mult {roofMultiplier != null ? roofMultiplier.toFixed(3) : '—'}
                                {multiplierDetail.meanSolarAccess != null
                                    ? ` · ${multiplierDetail.meanSolarAccess.toFixed(1)}% SA`
                                    : null}
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="pci-roof-modules__title">
                            <strong>
                                {layout.label}
                                {inactive ? ' (inactive)' : ''}
                            </strong>
                            <span>
                                {activeCount}/{presentModules.length} active
                            </span>
                            <span
                                className="pci-roof-modules__mult"
                                title={
                                    multiplierDetail.meanSolarAccess != null
                                        ? `Avg SA ${multiplierDetail.meanSolarAccess.toFixed(1)}% across ${multiplierDetail.includedCount} included module${multiplierDetail.includedCount === 1 ? '' : 's'}`
                                        : 'Multiplier for included modules on this roof'
                                }
                            >
                                Mult {roofMultiplier != null ? roofMultiplier.toFixed(3) : '—'}
                                {multiplierDetail.meanSolarAccess != null ? (
                                    <em className="pci-roof-modules__mult-sa">
                                        {multiplierDetail.meanSolarAccess.toFixed(1)}% SA
                                    </em>
                                ) : null}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={`pci-roof-modules__active-btn${
                                inactive
                                    ? ' pci-roof-modules__active-btn--off'
                                    : ' pci-roof-modules__active-btn--on'
                            }`}
                            aria-pressed={!inactive}
                            disabled={roofToggleDisabled}
                            title={
                                roofToggleDisabled
                                    ? 'Every panel on this roof is excluded by shaded-panel settings'
                                    : inactive
                                      ? 'Select all modules on this roof'
                                      : 'Deselect all modules on this roof'
                            }
                            onClick={
                                readOnly ? (event) => event.stopPropagation() : onToggleRoof
                            }
                        >
                            {inactive ? 'Inactive' : 'Active'}
                        </button>
                    </>
                )}
            </header>
            <div className="pci-roof-modules__scroller">
                <div className="pci-roof-modules__grid">
                    {layout.modules.map((cell, index) => {
                    if (!cell) {
                        return <span key={`gap-${layout.roofIndex}-${index}`} className="pci-roof-module pci-roof-module--gap" />;
                    }
                    const shadePending = isPreContractShadePending(cell.solarAccess);
                    const color = getPreContractModuleShadeColor(cell);
                    const moduleNum = cell.id.replace(/^R\d+M/, '');
                    const blocked = isBlocked(cell.solarAccess);
                    const productionKwh = moduleProductionKwh(cell);
                    return (
                        <button
                            key={cell.id}
                            type="button"
                            className={`pci-roof-module${
                                shadePending ? ' pci-roof-module--loading' : ''
                            }${cell.included ? '' : ' pci-roof-module--hatched'}${
                                blocked ? ' pci-roof-module--blocked' : ''
                            }`}
                            style={
                                shadePending
                                    ? undefined
                                    : {
                                          backgroundColor: color,
                                          ['--pci-sa' as string]: color,
                                      }
                            }
                            title={
                                shadePending
                                    ? `${cell.id} · Loading`
                                    : blocked
                                      ? `${cell.id} · ${cell.solarAccess}% SA · ${
                                            isPreContractPurplePanel(cell.solarAccess)
                                                ? 'purple panel excluded'
                                                : 'grey panel excluded'
                                        }`
                                      : `${cell.id} · ${cell.solarAccess}% SA${
                                            cell.multiplier != null &&
                                            Number.isFinite(cell.multiplier) &&
                                            cell.multiplier > 0
                                                ? ` · Mult ${cell.multiplier.toFixed(3)}`
                                                : ''
                                        }${
                                            productionKwh != null
                                                ? ` · ${formatKwh(productionKwh)}`
                                                : ''
                                        }`
                            }
                            aria-disabled={blocked || readOnly}
                            onClick={
                                readOnly
                                    ? (event) => event.stopPropagation()
                                    : () => onToggle(index)
                            }
                            onMouseEnter={readOnly ? undefined : () => onHover(index)}
                        >
                            <span className="pci-roof-module__label">
                                {compact ? moduleNum : `M${moduleNum}`}
                            </span>
                        </button>
                    );
                })}
                </div>
            </div>
        </div>
    );
}

function SiteTreeBoard({ trees, onToggle }: SiteTreeBoardProps) {
    const [showAll, setShowAll] = useState(false);

    const visibleTrees = useMemo(() => {
        const ranked = [...trees].sort((a, b) => {
            const am = Number(a.multiplier);
            const bm = Number(b.multiplier);
            const aImpact = Number.isFinite(am) ? am : 0;
            const bImpact = Number.isFinite(bm) ? bm : 0;
            return bImpact - aImpact;
        });
        if (showAll) return ranked;
        return ranked.filter((tree) => {
            const multiplier = Number(tree.multiplier);
            return Number.isFinite(multiplier) && multiplier > 0;
        });
    }, [trees, showAll]);

    const standingCount = visibleTrees.filter((tree) => !tree.ghosted).length;
    const hiddenCount = Math.max(0, trees.length - visibleTrees.length);

    return (
        <>
            {trees.length === 0 ? (
                <p className="pci-module-step__empty-roofs">
                    Waiting for trees from the 3D viewport…
                </p>
            ) : visibleTrees.length === 0 ? (
                <p className="pci-module-step__empty-roofs">
                    No impact trees yet.{' '}
                    <button
                        type="button"
                        className="pci-tree-board__link"
                        onClick={() => setShowAll(true)}
                    >
                        Show all {trees.length}
                    </button>
                </p>
            ) : (
                <div className="pci-roof-modules pci-tree-board">
                    <header className="pci-roof-modules__head">
                        <div className="pci-roof-modules__title">
                            <strong>{showAll ? 'All trees' : 'Impact trees'}</strong>
                            <span>
                                {standingCount}/{visibleTrees.length} standing
                                {hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="pci-tree-board__link"
                            onClick={() => setShowAll((on) => !on)}
                        >
                            {showAll ? 'Impact only' : 'Show all'}
                        </button>
                    </header>
                    <div className="pci-tree-board__grid">
                        {visibleTrees.map((tree) => (
                            <SiteTreeIcon key={tree.id} tree={tree} onToggle={onToggle} />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

function SiteTreeIcon({ tree, onToggle }: SiteTreeIconProps) {
    const handleClick = () => onToggle(tree.id);
    const multiplierLabel =
        typeof tree.multiplier === 'number' && Number.isFinite(tree.multiplier)
            ? tree.multiplier.toFixed(3)
            : '—';
    const name = tree.label || tree.id;
    return (
        <button
            type="button"
            className={`pci-site-tree${tree.ghosted ? ' pci-site-tree--ghosted' : ''}`}
            title={
                tree.ghosted
                    ? `${name} · ${multiplierLabel} · ghosted — click to restore`
                    : `${name} · ${multiplierLabel} · standing — click to ghost`
            }
            aria-pressed={tree.ghosted}
            onClick={handleClick}
        >
            <span className="pci-site-tree__id">{name}</span>
            <span className="pci-site-tree__multi">{multiplierLabel}</span>
        </button>
    );
}

export default PreContractModuleStep;
